/**
 * Keep the site header's nav item in step with the pick.
 *
 * On woodsidebible.org the panel renders inside the theme's "Locations"
 * menu item (a Salient mega menu). Once a campus is chosen that item should
 * read as the campus: its pin icon becomes the campus art and its label
 * becomes the congregation's name ("Troy"), condensing to the short name
 * ("TR") only where the desktop menu bar is too tight for it. The item is
 * found from the widget's own position in the DOM, and every copy of it
 * (Salient repeats menu items for the phone header and the off-canvas menu)
 * is updated, so no IDs are hard-coded.
 */

import type { SelectedCampus } from './campus-storage';

const ICON_CLASS = 'campus-picker-nav-art';
const ART_WRAP_CLASS = 'campus-picker-nav-art-wrap';
const LIVE_CLASS = 'campus-picker-nav-live';
const FULL_CLASS = 'campus-picker-nav-full';
const SHORT_CLASS = 'campus-picker-nav-short';
const STYLE_ID = 'campus-picker-nav-style';
/**
 * Below this the desktop menu bar runs out of room for a long campus name
 * next to the other header items, so the two-letter code takes over. Under
 * the theme's 1000px mobile breakpoint the item lives in the off-canvas menu
 * with a full row to itself, and that copy always shows the name.
 */
const CONDENSE_BELOW_PX = 1200;

export interface NavItemTargets {
  labels: HTMLElement[];
  icons: HTMLElement[];
  /** The item's own link, so a pick can point it at the campus page */
  links: HTMLAnchorElement[];
}

/** The menu-item-N class shared by every copy of the nav item the container sits in */
function menuItemClass(container: HTMLElement): string | null {
  const host =
    container.getRootNode() instanceof ShadowRoot
      ? ((container.getRootNode() as ShadowRoot).host as HTMLElement)
      : container;
  const li = host.closest('li.menu-item');
  if (!li) return null;
  return Array.from(li.classList).find((c) => /^menu-item-\d+$/.test(c)) ?? null;
}

/**
 * Resolve the label and icon elements to keep in sync. Explicit selectors
 * win; otherwise the nav item is the one the widget renders inside.
 */
export function findNavItemTargets(
  container: HTMLElement,
  labelSelector?: string,
  iconSelector?: string
): NavItemTargets {
  if (labelSelector || iconSelector) {
    return {
      labels: labelSelector
        ? Array.from(document.querySelectorAll<HTMLElement>(labelSelector))
        : [],
      icons: iconSelector ? Array.from(document.querySelectorAll<HTMLElement>(iconSelector)) : [],
      links: [],
    };
  }
  const cls = menuItemClass(container);
  if (!cls) return { labels: [], icons: [], links: [] };
  const items = Array.from(document.querySelectorAll<HTMLElement>(`li.${cls}`));
  const labels: HTMLElement[] = [];
  const icons: HTMLElement[] = [];
  const links: HTMLAnchorElement[] = [];
  for (const li of items) {
    const link = li.querySelector<HTMLAnchorElement>(':scope > a');
    if (!link) continue;
    links.push(link);
    const label = link.querySelector<HTMLElement>('.menu-title-text');
    if (label) labels.push(label);
    const icon = link.querySelector<HTMLElement>('.nectar-menu-icon, i.fa');
    if (icon) icons.push(icon);
  }
  return { labels, icons, links };
}

/**
 * The label's stylesheet lives in the page, not the widget's shadow root,
 * because the nav item is the theme's. Only the copy inside the desktop menu
 * bar (#header-outer .sf-menu) ever condenses.
 */
function ensureNavStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = [
    `.${SHORT_CLASS}{display:none}`,
    `@media (max-width:${CONDENSE_BELOW_PX - 1}px){`,
    `#header-outer .sf-menu .${FULL_CLASS}{display:none}`,
    `#header-outer .sf-menu .${SHORT_CLASS}{display:inline}`,
    `}`,
    // The campus art. The theme gives header images a bottom margin, which
    // floats the art above the label; the wrapper's margin and align-self
    // centre it on the text whether the link lays out as flex or inline.
    `.${ART_WRAP_CLASS}{position:relative;display:inline-flex;align-self:center;vertical-align:middle;margin:0 .5em 0 0}`,
    // Live dot at the art's bottom-right: a solid red dot with a white ring,
    // and a fainter one pinging out behind it (the widget's own LIVE pill).
    `.${LIVE_CLASS}{position:absolute;right:-3px;bottom:-2px;width:10px;height:10px;display:none;align-items:center;justify-content:center;pointer-events:none}`,
    `.${ART_WRAP_CLASS}[data-live="1"] .${LIVE_CLASS}{display:flex}`,
    `.${LIVE_CLASS}::before{content:"";position:absolute;inset:1px;border-radius:9999px;background:#ef4444;opacity:.75;animation:campus-picker-ping 1s cubic-bezier(0,0,.2,1) infinite}`,
    `.${LIVE_CLASS}::after{content:"";position:relative;width:8px;height:8px;border-radius:9999px;background:#dc2626;box-shadow:0 0 0 2px #fff}`,
    `@keyframes campus-picker-ping{75%,100%{transform:scale(2);opacity:0}}`,
    `@media (prefers-reduced-motion:reduce){.${LIVE_CLASS}::before{animation:none}}`,
  ].join('');
  document.head.appendChild(style);
}

function span(className: string, text: string): HTMLSpanElement {
  const el = document.createElement('span');
  el.className = className;
  el.textContent = text;
  return el;
}

/** MP's slug keeps accents ("troy-español"); WordPress strips them ("troy-espanol") */
function pageSlug(slug: string): string {
  return slug
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Write the pick into the nav item: the campus name for the label (its short
 * name where the menu bar is tight), campus art for the icon, and the campus
 * page for the link (hover opens the panel, a click goes to your campus; with
 * nothing picked the link stays on all locations).
 */
export function syncNavItem(
  targets: NavItemTargets,
  campus: SelectedCampus | null,
  detailsBaseUrl?: string
): void {
  for (const link of targets.links) {
    if (!link.dataset.campusPickerOriginalHref)
      link.dataset.campusPickerOriginalHref = link.getAttribute('href') ?? '';
    if (campus?.slug && detailsBaseUrl) {
      link.href = `${detailsBaseUrl.replace(/\/$/, '')}/${pageSlug(campus.slug)}/`;
    } else {
      link.href = link.dataset.campusPickerOriginalHref;
    }
  }
  if (!campus) return;
  ensureNavStyles();
  for (const label of targets.labels) {
    if (!label.dataset.campusPickerOriginal)
      label.dataset.campusPickerOriginal = label.textContent ?? '';
    const children = [span(FULL_CLASS, campus.name)];
    if (campus.shortName && campus.shortName !== campus.name)
      children.push(span(SHORT_CLASS, campus.shortName));
    label.replaceChildren(...children);
  }
  for (const icon of targets.icons) {
    let wrap = icon.previousElementSibling as HTMLElement | null;
    if (!wrap || !wrap.classList.contains(ART_WRAP_CLASS)) {
      if (!campus.svgUrl) continue;
      wrap = document.createElement('span');
      wrap.className = ART_WRAP_CLASS;
      const art = document.createElement('img');
      art.className = ICON_CLASS;
      art.setAttribute('alt', '');
      art.setAttribute('aria-hidden', 'true');
      // Inline on purpose: Salient's `.row .col img { height: auto; max-width:
      // 100% }` outranks a class selector, and inside a zero-width wrapper
      // that collapses the art to nothing.
      art.style.cssText = 'display:block;height:1.5em;width:auto;max-width:none;margin:0;';
      const dot = document.createElement('span');
      dot.className = LIVE_CLASS;
      dot.setAttribute('title', 'Your campus is live now');
      wrap.append(art, dot);
      icon.before(wrap);
    }
    const art = wrap.querySelector<HTMLImageElement>(`.${ICON_CLASS}`);
    if (campus.svgUrl && art) {
      art.src = campus.svgUrl;
      wrap.style.display = '';
      icon.style.display = 'none';
    } else {
      wrap.style.display = 'none';
      icon.style.display = '';
    }
  }
}

/**
 * Light the dot at the art's bottom-right while the selected campus is
 * streaming. Called with each live-status poll and each campus change.
 */
export function syncNavLive(targets: NavItemTargets, isLive: boolean): void {
  for (const icon of targets.icons) {
    const wrap = icon.previousElementSibling as HTMLElement | null;
    if (!wrap || !wrap.classList.contains(ART_WRAP_CLASS)) continue;
    if (isLive) wrap.dataset.live = '1';
    else delete wrap.dataset.live;
  }
}
